import { Location } from '@angular/common';
import {
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import {
  addDays,
  addHours,
  addMinutes,
  format,
  getUnixTime,
  startOfDay,
} from 'date-fns';
import getDay from 'date-fns/getDay';
import _ from 'lodash';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import { getArchiveAt } from 'server/helpers/getArchiveAt';
import { getTimeSinceJobFinished } from 'server/helpers/getTimeSinceJobFinished';
import {
  setConditionalTransfersForJob,
  setTransfersForJob,
} from 'server/helpers/transfer-helpers';
import { Certificates } from 'server/shared/collections/certificates';
import { Customers } from 'server/shared/collections/customers';
import { Jobs } from 'server/shared/collections/jobs';
import { Organisations } from 'server/shared/collections/organisations';
import { Specialities } from 'server/shared/collections/specialities';
import { Users } from 'server/shared/collections/users';
import { checkCanCancelJob } from 'server/shared/helpers/checkCanCancelJob';
import { getJobCounts } from 'server/shared/helpers/getJobCounts';
import { getJobStart } from 'server/shared/helpers/getJobStart';
import {
  DispoJobStatus,
  getJobStatusForDispo,
} from 'server/shared/helpers/getJobStatusForDispo';
import { randomId } from 'server/shared/helpers/random';
import {
  addTimeZoneOffset,
  getNewUnixTimestamp,
} from 'server/shared/helpers/removeTimeZoneOffset';
import { formatTimeNumber } from 'server/shared/helpers/time';
import { AssignmentDefault } from 'server/shared/models/assignmentDefault';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { Certificate } from 'server/shared/models/certificate';
import {
  CustomCriteria,
  CustomCriteriaOnJobSelect,
  CustomCriteriaOnJobToggle,
  CustomCriteriaType,
} from 'server/shared/models/customCriteria';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { CustomerInternalInformation, Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { LanguageEnum } from 'server/shared/models/language.enum';
import { PoolerType } from 'server/shared/models/poolerType';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { Shift } from 'server/shared/models/shift';
import { Speciality } from 'server/shared/models/speciality';
import { Tag } from 'server/shared/models/tag';
import { Transfer } from 'server/shared/models/transfer';
import { DispoUser, PoolerUser, User } from 'server/shared/models/user';
import {
  getWeekDayEnumByIndex,
  WeekDayEnum,
} from 'server/shared/models/weekday.enum';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { JobRequestService } from 'src/app/services/job-request.service';
import {
  filterIsNotEmpty,
  rxFilterIsNotEmpty,
} from 'src/helpers/filterIsNotEmpty';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { replaceTranslationVariables } from 'src/helpers/translationHelpers';
import {
  checkMultiDayRest,
  RestViolationResult,
  ViolationsPerDayUuid,
} from '../../../helpers/checkMultiDayRest';
import { getInviteesOfJob } from '../../../helpers/getInviteesOfJob';
import { getPossibleUsersForJob } from '../../../helpers/job/getPossibleUsersForJob';
import { processBookingWarnings } from '../../../helpers/job/processBookingWarnings';
import {
  DecodedMultiDaySelection,
  urlDecodeMultiDaySelection,
} from '../../../helpers/job/urlEncodeMultiDaySelection';
import { MultiDay } from '../../../types/job/MultiDay';
import { CustomerService } from '../../services/customer.service';
import { JobEditService } from '../../services/job-edit.service';
import { OrganisationService } from '../../services/organisation.service';
import { PermissionsService } from '../../services/permissions.service';
import { PoolerService } from '../../services/pooler/pooler.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import { ConfirmationDialog } from '../../shared/confirmation-dialog/confirmation.component';
import { getDatesFromRanges } from '../../shared/date-range-select/getDatesFromRanges';
import { sortDateRanges } from '../../shared/date-range-select/sortDateRanges';
import { DateRange } from '../../shared/date-ranges-input/date-ranges-input.component';
import { UnsavedChangesDeactivate } from '../../shared/guards/unsaved-changes-deactivate.interface';
import { JobPossibleUsersListComponent } from '../../shared/job-possible-users-list/job-possible-users-list.component';
import { SubscriptionService } from '../../shared/subscription.service';
import { AgentsInfoDialog } from './dialogs/agents-info-dialog/agents-info-dialog.component';
import { InviteeToggledEvent, JobMatchPoolerUser } from './job-edit.types';

// when data changes in a live query, this selection is attempted to be applied again as far as possible
type SelectionCache = {
  selectedUsers: JobMatchPoolerUser[];
  directAcceptUsers: JobMatchPoolerUser[];
  multiDays: MultiDay[];
};

@Component({
  selector: 'job-edit',
  templateUrl: 'job-edit.component.html',
  styleUrls: ['job-edit.component.scss'],
})
export class JobEditComponent
  implements OnInit, OnDestroy, UnsavedChangesDeactivate
{
  destroyed$ = new Subject<void>();

  jobUsersSubscription?: Subscription;
  jobSubscription?: Subscription;

  // rxjs refactored props
  jobId$ = this.route.params.pipe(
    map((p) => p.jobId),
    distinctUntilChanged(),
  );

  isNewJob$ = this.jobId$.pipe(
    map((jobId) => jobId === 'new'),
    distinctUntilChanged(),
  );
  dispoCustomer$ = this.customerService.readyDispoCustomer$;

  // legacy props
  jobId: string = '';
  initialJob?: Job;
  job: Job = { department: {}, createdBy: {}, customer: {} } as Job;
  jobSource$ = new BehaviorSubject<Job | undefined>(undefined);
  job$ = this.jobSource$.pipe(rxFilterIsNotEmpty(), rxDistinctJSONStringify());

  customerInternalInformation: CustomerInternalInformation =
    {} as CustomerInternalInformation;
  demandReasonId?: string;

  readonly customCriteriaTypeEnum = CustomCriteriaType;

  compareCustomCriteriaOption = (
    a: { id: string },
    b: { id: string },
  ): boolean => !!a && !!b && a.id === b.id;

  sexEnum = SexEnum;
  inviteeStatusEnum = InviteeStatusEnum;
  jobStatusEnum = JobStatusEnum;
  roleEnum = RoleEnum;

  undecided?: boolean;
  ignoreUserPreferenceDepartmentBlacklist = false;
  hasToggledUndecided = false;
  hasToggledExternalInvitees = false;
  undecidedTimeout?: NodeJS.Timeout;
  externalInviteesTimeout?: NodeJS.Timeout;
  selectedUsers: JobMatchPoolerUser[] = [];
  directAcceptUsers: JobMatchPoolerUser[] = [];

  selectedDepartment$ = new BehaviorSubject<Department | undefined>(undefined);
  get selectedDepartment(): Department | undefined {
    return this.selectedDepartment$.value;
  }
  set selectedDepartment(value: Department | undefined) {
    this.selectedDepartment$.next(value);
  }

  organisationTags: Tag[] = [];
  possibleUsers: JobMatchPoolerUser[] = [];
  dispoUsers?: Observable<DispoUser[]>;
  acceptedUsers?: Observable<PoolerUser[]>;
  availableUsers?: Observable<PoolerUser[]>;
  isNewJob: boolean = false;

  isArchivedJob: boolean = false;
  hasMultipleShifts: boolean = false;

  dateRanges: DateRange[] = [];

  cachedSelection?: SelectionCache;

  submitted = false;
  i18n = this.translationService.getTranslations('jobs');
  lang = this.translationService.getLanguage();

  user?: DispoUser;
  loggedInCustomer?: Customer;
  sharedPoolCustomers: Customer[] = [];

  customerOfJob$ = new BehaviorSubject<Customer | undefined>(undefined);
  usersTracker?: Tracker.Computation;
  userTracker?: Tracker.Computation;
  jobTracker?: Tracker.Computation;

  suggestInsteadDirectAccept: boolean | undefined = undefined;

  hasAvailableOrAcceptedInvitees$ = new BehaviorSubject<boolean>(false);
  set hasAvailableOrAcceptedInvitees(value: boolean) {
    this.hasAvailableOrAcceptedInvitees$.next(value);
  }
  get hasAvailableOrAcceptedInvitees(): boolean {
    return this.hasAvailableOrAcceptedInvitees$.value;
  }

  hasAcceptedInvitee$ = new BehaviorSubject<boolean>(false);
  set hasAcceptedInvitee(value: boolean) {
    this.hasAcceptedInvitee$.next(value);
  }
  get hasAcceptedInvitee(): boolean {
    return this.hasAcceptedInvitee$.value;
  }

  loadingUsers: boolean = false;
  loadingJob: boolean = false;
  meteorLiveQueryHandlers: Meteor.LiveQueryHandle[] = [];
  selectedDepartmentId?: string;

  multiDay: boolean = false;
  multiDays: MultiDay[] = [];
  multiDayDirectAcceptUsers = 0;
  multiDayInviteUsers = 0;

  now = new Date();

  branch?: string;
  editor?: string;

  randomId?: string;

  userCount: Map<string, number> = new Map();

  carLicenseEnum = CarLicenseEnum;
  languageEnum = LanguageEnum;
  selectedWeekDays$ = new BehaviorSubject<WeekDayEnum[]>([]);
  get selectedWeekDays(): WeekDayEnum[] {
    return this.selectedWeekDays$.value;
  }
  set selectedWeekDays(value: WeekDayEnum[]) {
    this.selectedWeekDays$.next(value);
  }
  jobTypeEnum = JobTypeEnum;

  editEnabled = false;

  isOwnedOrganisationJob$ = combineLatest([
    this.dispoCustomer$,
    this.job$,
  ]).pipe(
    map(
      ([dispoCustomer, job]) =>
        dispoCustomer?._id !== job?.customer?.id &&
        job?.ownerCustomerIds?.some((owner) => owner === dispoCustomer?._id),
    ),
  );

  finishedLoadingQueryParams = true;

  isSaving = false;

  cancelUser?: User;

  archivedJobsSubscription?: Subscription;

  transferToMe?: Transfer;
  hasTransferred = false;
  transferConfirmed = false;
  selectedTransferConfirmationPerson?: string;

  hasSharedPoolInvitees = false;

  paramUserId?: string;
  paramBookDirectly: boolean = false;
  paramCertificates: string[] = [];
  paramSpecialities: string[] = [];
  hasParamUsers = false;

  @ViewChild('jobForm', { static: false }) form?: NgForm;
  @ViewChild('availableUsers', { static: false })
  availableUsersList?: JobPossibleUsersListComponent;
  @ViewChildren('multiDayAvailableUsers')
  multiDayAvailableUsersLists?: QueryList<JobPossibleUsersListComponent>;
  customerReady$ = new BehaviorSubject(false);
  usersReady$ = new BehaviorSubject(false);

  certificateSet = new Set();
  certificatesString: string = '';
  specialitySet = new Set();
  specialityString: string = '';

  jobInPast = false;
  userSearch: string = '';
  violationMessagesPerDayUuid: Map<
    string,
    {
      overlapsMessage: string;
      restViolationsMessage: string;
      overlaps: RestViolationResult[];
      restViolations: RestViolationResult[];
    }
  > = new Map();

  transferAll: boolean = false;
  transferredToAll: boolean = false;
  oldTransferIsAutoByCustomerId: Map<string, boolean> = new Map<
    string,
    boolean
  >();

  jobCounts$ = combineLatest([
    this.job$,
    this.dispoCustomer$,
    this.customerService.sharedPoolCustomers$,
  ]).pipe(
    map(([job, customer, sharedPoolCustomers]) =>
      getJobCounts(job, customer, sharedPoolCustomers),
    ),
  );

  showCompressedJobInfo = false;
  dispoJobStatus$ = combineLatest([
    this.job$,
    this.jobCounts$,
    this.dispoCustomer$,
  ]).pipe(
    map(([job, counts, customer]) => {
      if (!job) {
        return DispoJobStatus.OPEN;
      }
      if (!counts || !customer) return;
      return getJobStatusForDispo(job, counts, customer);
    }),
    shareReplay(1),
  );

  private _isOwnJob(): boolean {
    return !(
      !this.isNewJob &&
      this.loggedInCustomer &&
      this.job.customer.id != this.loggedInCustomer._id
    );
  }

  isOwnJob(job?: Job, loggedInCustomer?: Customer): boolean {
    return job?.customer?.id === loggedInCustomer?._id;
  }
  isOpenJob$ = this.job$.pipe(map((job) => job?.status === JobStatusEnum.OPEN));

  canEditRequiredUsersOnClosedJob$ = combineLatest([
    this.job$,
    this.dispoCustomer$,
  ]).pipe(
    map(
      ([job, dispoCustomer]) =>
        job?.status === JobStatusEnum.CLOSED &&
        this.isOwnJob(job, dispoCustomer),
    ),
  );

  hasTransferredExternal$ = combineLatest([
    this.job$,
    this.dispoCustomer$,
  ]).pipe(
    map(
      ([job, dispoCustomer]) =>
        job &&
        this.isOwnJob(job, dispoCustomer) &&
        job.transfers?.some(
          (t) => t.secret && t.transferredAt && !t.externalStillDraft,
        ),
    ),
  );

  criteriaDisabled$ = combineLatest([
    this.isOpenJob$,
    this.hasTransferredExternal$,
  ]).pipe(
    map(
      ([isOpenJob, hasTransferredExternal]) =>
        !isOpenJob || hasTransferredExternal,
    ),
  );

  datePickerDisabled$ = this.isNewJob$.pipe(map((isNewJob) => !isNewJob));

  canEditShifts$ = combineLatest([
    this.hasAvailableOrAcceptedInvitees$,
    this.job$,
    this.dispoCustomer$,
    this.hasTransferredExternal$,
  ]).pipe(
    map(
      ([
        hasAvailableOrAcceptedInvitees,
        job,
        loggedInCustomer,
        hasTransferredExternal,
      ]) => {
        return (
          (!hasAvailableOrAcceptedInvitees ||
            job.status === JobStatusEnum.ARCHIVED) &&
          job.customer?.id === loggedInCustomer?._id &&
          !hasTransferredExternal
        );
      },
    ),
  );

  canEditJobTypes$ = this.canEditShifts$;

  isSharedJob$ = combineLatest([this.job$, this.dispoCustomer$]).pipe(
    map(
      ([job, dispoCustomer]) =>
        !this.isOwnJob(job, dispoCustomer) &&
        !job?.transfers?.some(
          (transfer) =>
            transfer.targetCustomer?.id === this.loggedInCustomer?._id &&
            transfer.transferredAt,
        ),
    ),
  );

  get enabledJobs(): number {
    return this.multiDays.filter((singleDay) => singleDay.enabled).length;
  }

  canClose$ = combineLatest([
    this.isNewJob$,
    this.job$,
    this.hasAcceptedInvitee$,
    this.dispoCustomer$,
  ]).pipe(
    map(([isNewJob, job, hasAcceptedInvitee, dispoCustomer]) => {
      return (
        !isNewJob &&
        hasAcceptedInvitee &&
        job &&
        job.status == JobStatusEnum.OPEN
      );
    }),
  );

  get canDuplicate(): boolean {
    return (
      (!this.isNewJob &&
        this.job &&
        this.job.customer?.id === this.loggedInCustomer?._id) ||
      false
    );
  }

  get canCancel(): boolean {
    return (
      (!this.isNewJob &&
        this.loggedInCustomer &&
        checkCanCancelJob(this.job, this.loggedInCustomer)) ||
      false
    );
  }

  get canDecline(): boolean {
    const targetTransfer = this.job?.transfers?.find(
      (transfer) => transfer.targetCustomer.id === this.loggedInCustomer?._id,
    );
    const hasBookedInvitee = this.job?.invitees?.some(
      (invitee) =>
        invitee.status === InviteeStatusEnum.BOOKED &&
        invitee.customer?.id === this.loggedInCustomer?._id,
    );
    return (
      (!this.isNewJob &&
        this.job &&
        this.job.status != JobStatusEnum.ARCHIVED &&
        this.job.status != JobStatusEnum.CANCELED &&
        targetTransfer &&
        targetTransfer.transferredAt &&
        !targetTransfer.declinedAt &&
        !hasBookedInvitee) ||
      false
    );
  }

  get shiftsValid(): boolean {
    return this.job?.shifts?.every((shift) => shift.from != shift.to);
  }

  selectedDepartmentShifts$ = this.selectedDepartment$.pipe(
    map((selectedDepartment) => {
      if (!selectedDepartment) {
        return [];
      }
      return selectedDepartment.shifts;
    }),
  );

  jobType$ = this.job$.pipe(map((job) => job.jobType));

  availableShifts$ = combineLatest([
    this.selectedDepartmentShifts$,
    this.selectedWeekDays$,
    this.jobType$,
  ]).pipe(
    map(([selectedDepartmentShifts, selectedWeekDays, jobType]) => {
      return selectedDepartmentShifts
        .filter((shift) => this.shiftMatchesWeedkays(shift, selectedWeekDays))
        .filter((shift) => this.shiftMatchesJobType(shift, jobType))
        .sort((a, b) => a.from - b.from);
    }),
  );

  // has shifts, but not for this weekday
  noShiftsForWeekDaysHintVisible$ = combineLatest([
    this.selectedDepartmentShifts$,
    this.selectedWeekDays$,
  ]).pipe(
    map(([selectedDepartmentShifts, selectedWeekDays]) => {
      const availableWeekDayShifts = selectedDepartmentShifts.filter((shift) =>
        this.shiftMatchesWeedkays(shift, selectedWeekDays),
      );
      return (
        selectedDepartmentShifts.length > 0 &&
        availableWeekDayShifts.length === 0
      );
    }),
  );

  noShiftsForJobTypeHintVisible$ = combineLatest([
    this.selectedDepartmentShifts$,
    this.jobType$,
  ]).pipe(
    map(([selectedDepartmentShifts, jobType]) => {
      const availableJobTypeShifts = selectedDepartmentShifts.filter((shift) =>
        this.shiftMatchesJobType(shift, jobType),
      );
      return (
        selectedDepartmentShifts.length > 0 &&
        availableJobTypeShifts.length === 0
      );
    }),
  );

  noShiftsAvailableHintVisible$ = this.selectedDepartment$.pipe(
    map((department) => {
      return department && department.shifts.length === 0;
    }),
  );

  shiftMatchesWeedkays(shift: Shift, weekDays: WeekDayEnum[]): boolean {
    return (
      !shift.specificWeekDays ||
      shift.specificWeekDays.length === 0 ||
      // keep shifts that are applicable to EVERY selected weekday
      weekDays.every((weekDay) => shift.specificWeekDays?.includes(weekDay))
    );
  }

  shiftMatchesJobType(shift: Shift, jobType: JobTypeEnum): boolean {
    return (
      !shift.specificJobTypes ||
      shift.specificJobTypes.length === 0 ||
      shift.specificJobTypes.includes(jobType)
    );
  }

  getShiftsForWeekday(weekDay: WeekDayEnum): Shift[] {
    return (
      this.selectedDepartment?.shifts
        .filter((shift) => {
          return (
            !shift.specificWeekDays ||
            shift.specificWeekDays.length === 0 ||
            shift.specificWeekDays?.includes(weekDay)
          );
        })
        .sort((a, b) => a.from - b.from) || []
    );
  }

  isReadOnly$ = combineLatest([
    this.job$,
    this.isNewJob$,
    this.dispoCustomer$,
    this.permissionsService.completePermissions$,
  ]).pipe(
    map(
      ([job, isNewJob, dispoCustomer, dispoPermissions]) =>
        !isNewJob &&
        (!dispoCustomer ||
          !job.department ||
          !this.permissionsService.userCanEditJob(job, dispoPermissions) ||
          !this.permissionsService.customerCanEditJob(dispoCustomer, job)),
    ),
    distinctUntilChanged(),
  );

  requiredEmployeesDisabled$ = combineLatest([
    this.isReadOnly$,
    this.isOpenJob$,
    this.canEditRequiredUsersOnClosedJob$,
  ]).pipe(
    map(
      ([isReadOnly, isOpenJob, canEditOnClosedJob]) =>
        isReadOnly || (!isOpenJob && !canEditOnClosedJob),
    ),
  );

  poolerTypes: PoolerType[] = [];

  constructor(
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private toastService: ToastService,
    private translationService: TranslationService,
    private customerService: CustomerService,
    private subscriptionService: SubscriptionService,
    private dialog: MatDialog,
    private poolerService: PoolerService,
    private permissionsService: PermissionsService,
    public jobService: JobEditService,
    private organisationService: OrganisationService,
    private userService: UserService,
    public jobRequestService: JobRequestService,
  ) {
    this.subscriptionService.organisations.ready$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();

    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.poolerTypes = customer?.poolerTypes || [];
      });
  }

  initJobByIdEffect$ = this.jobId$.pipe(
    tap((jobId) => this.initJob(jobId)),
    takeUntil(this.destroyed$),
  );

  ngOnInit() {
    this.initJobByIdEffect$.subscribe();

    this.userTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.user = Users.findOne({
          _id: Meteor.userId(),
          role: RoleEnum.DISPO,
        }) as DispoUser;
      });
    });
  }

  updateCachedSelection() {
    this.cachedSelection = JSON.parse(
      JSON.stringify({
        selectedUsers: this.selectedUsers,
        directAcceptUsers: this.directAcceptUsers,
        multiDays: this.multiDays,
      }),
    );
  }

  applyCachedSelectionForMultiDay(multiDay: MultiDay) {
    if (!this.cachedSelection) {
      return;
    }
    const dayCache = this.cachedSelection.multiDays.find(
      (d) => d.uuid === multiDay.uuid,
    );
    multiDay.directAcceptUsers =
      dayCache?.directAcceptUsers
        .map((cacheUser) =>
          multiDay.possibleUsers.find(
            (newPossibleUser) =>
              newPossibleUser._id === cacheUser._id &&
              newPossibleUser.canBeBookedDirectly,
          ),
        )
        .filter(filterIsNotEmpty) || [];
    multiDay.inviteUsers =
      dayCache?.inviteUsers
        .map((cacheUser) =>
          multiDay.possibleUsers.find(
            (newPossibleUser) => newPossibleUser._id === cacheUser._id,
          ),
        )
        .filter(filterIsNotEmpty) || [];
  }

  applyCachedSelectionForMultiDays() {
    this.multiDays.forEach((multiDay) => {
      this.applyCachedSelectionForMultiDay(multiDay);
    });
    this.updateCachedSelection();
  }

  applyCachedSelectionForSingleDay() {
    this.selectedUsers = this.possibleUsers.filter((possibleUser) =>
      this.cachedSelection?.selectedUsers.some(
        (selectedUser) => selectedUser._id === possibleUser._id,
      ),
    );
    this.directAcceptUsers = this.possibleUsers.filter((possibleUser) =>
      this.cachedSelection?.directAcceptUsers.some(
        (selectedUser) =>
          selectedUser._id === possibleUser._id &&
          possibleUser.canBeBookedDirectly,
      ),
    );
    this.updateCachedSelection();
  }

  resetState() {
    this.directAcceptUsers = [];
    this.selectedUsers = [];
    this.cachedSelection = undefined;
    this.multiDays = [];
    this.possibleUsers = [];
    this.selectedDepartment = undefined;
    this.selectedDepartmentId = undefined;
    this.hasAvailableOrAcceptedInvitees = false;
    this.hasAcceptedInvitee = false;
    this.undecided = false;
  }

  trackByCustomCriteriaId(_index: number, def: CustomCriteria): string {
    return def.id;
  }

  getJobSingleSelectValue(
    criterion: CustomCriteriaOnJobSelect,
  ): { id: string; displayName: string } | undefined {
    return criterion.values?.[0];
  }

  setJobSingleSelectValue(
    criterion: CustomCriteriaOnJobSelect,
    opt: { id: string; displayName: string } | null | undefined,
  ): void {
    criterion.values = opt ? [opt] : [];
  }

  jobToggleCriterion(criteriaId: string): CustomCriteriaOnJobToggle {
    return this.customerInternalInformation.customCriteria![
      criteriaId
    ] as CustomCriteriaOnJobToggle;
  }

  jobSelectCriterion(criteriaId: string): CustomCriteriaOnJobSelect {
    return this.customerInternalInformation.customCriteria![
      criteriaId
    ] as CustomCriteriaOnJobSelect;
  }

  private mergeCustomerCustomCriteriaIntoJobInternalInfo(): void {
    if (!this.loggedInCustomer) return;
    if (!this.customerInternalInformation) {
      this.customerInternalInformation = {} as CustomerInternalInformation;
    }
    const info = this.customerInternalInformation;
    const customerCustomCriterias = this.loggedInCustomer.customCriteria;
    if (!customerCustomCriterias?.length) {
      info.customCriteria = undefined;
      return;
    }

    const existingCustomCriterias = info.customCriteria || {};
    const newCustomCriteria: NonNullable<
      CustomerInternalInformation['customCriteria']
    > = {};

    customerCustomCriterias.forEach((customerCustomCriteria) => {
      const existingCustomCriteria =
        existingCustomCriterias[customerCustomCriteria.id];
      if (customerCustomCriteria.type === CustomCriteriaType.TOGGLE) {
        if (existingCustomCriteria?.type === CustomCriteriaType.TOGGLE) {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.TOGGLE,
            value: existingCustomCriteria.value,
            // always get default value from customer
            defaultValueOnJob: customerCustomCriteria.defaultValueOnJob,
            defaultValueOnProfile: customerCustomCriteria.defaultValueOnProfile,
            blockingToggle: customerCustomCriteria.blockingToggle,
          };
        } else {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.TOGGLE,
            value: customerCustomCriteria.defaultValueOnJob,
            defaultValueOnJob: customerCustomCriteria.defaultValueOnJob,
            defaultValueOnProfile: customerCustomCriteria.defaultValueOnProfile,
            blockingToggle: customerCustomCriteria.blockingToggle,
          };
        }
      } else {
        if (existingCustomCriteria?.type === CustomCriteriaType.SELECT) {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.SELECT,
            values: existingCustomCriteria.values,
          };
        } else {
          newCustomCriteria[customerCustomCriteria.id] = {
            id: customerCustomCriteria.id,
            displayName: customerCustomCriteria.displayName,
            type: CustomCriteriaType.SELECT,
            values: [],
          };
        }
      }
    });

    info.customCriteria = newCustomCriteria;
    this.syncCustomerInternalInformationToJob();
  }

  private syncCustomerInternalInformationToJob(): void {
    if (!this.loggedInCustomer?._id || !this.job) {
      return;
    }
    this.job.customerInternalInformation = {
      ...this.job.customerInternalInformation,
      [this.loggedInCustomer._id]: this.customerInternalInformation,
    };
  }

  jobCustomCriteriaChanged(): void {
    this.filterUsers().then();
    this.form?.control.markAsDirty();
  }

  async initJob(jobId: string) {
    this.resetState();
    this.isNewJob = 'new' == jobId;
    this.showCompressedJobInfo = !this.isNewJob;

    this.dispoUsers = (
      Users.find({
        role: RoleEnum.DISPO,
        isIntemp: { $ne: true },
      }) as Observable<DispoUser[]>
    ).pipe(rxNgZoneRun(this.ngZone));

    const [user, customer] = await Promise.all([
      this.subscriptionService.getReadyDispoUser(),
      this.customerService.readyDispoCustomer,
    ]);
    this.user = user;
    this.loggedInCustomer = customer;
    this.mergeCustomerCustomCriteriaIntoJobInternalInfo();
    if (this.isNewJob) this.customerOfJob$.next(this.loggedInCustomer);
    if (!this.customerReady$.value) {
      this.customerReady$.next(true);
    }
    if (
      this.user.dispoPermissions?.dispoAccess?.featurePermissions
        ?.sharedPoolPermissions !== false
    ) {
      this.poolerService.readyJobSharedPoolCustomers$.subscribe((customers) => {
        this.sharedPoolCustomers = customers;

        if (!this.isNewJob) {
          this.filterUsers(true);
          if (this.job) {
            this.getInvitees();
          }
        }
      });
    }
    if (!this.isNewJob) {
      if (this.jobTracker) {
        this.jobTracker.stop();
      }
      this.jobSubscription?.unsubscribe();
      this.jobSubscription = MeteorObservable.subscribe('job', jobId).subscribe(
        () => {
          this.jobTracker = Tracker.autorun(() => {
            let job = Jobs.findOne(jobId);
            if (!job) {
              this.router.navigate(['dashboard']).then();
            }
            if (job && this.loggedInCustomer && this.user) {
              this.ngZone.run(async () => {
                // If this is a different job than the initial one, reset and fully load it
                if (!this.initialJob || this.initialJob._id !== job._id) {
                  this.initialJob = job;
                  this.job = job;
                } else {
                  this.loadingJob = true;
                  this.job.status = job.status;
                  this.job.invitees = [...job.invitees];
                  this.job.transfers = job.transfers;

                  Object.keys(job).forEach((key: string) => {
                    let jobKey = key as keyof Job;
                    if (job[jobKey] !== this.initialJob![jobKey]) {
                      // @ts-ignore
                      this.job![jobKey] = job[jobKey];
                    }
                  });
                  setTimeout(() => {
                    this.loadingJob = false;
                  });
                }
                this.initialJob = job;
                this.jobSource$.next(job);
                this.evaluateJobIsInPast();
                let customerOfJob = Customers.findOne(job.customer.id);
                this.customerOfJob$.next(customerOfJob);
                this.dateRanges = [{ from: new Date(this.job.date) }];

                if (!this.selectedDepartment) {
                  let departmentWithOrganisation =
                    await this.organisationService.getOrganisationAndDepartmentById(
                      job.department.id,
                    );
                  if (departmentWithOrganisation) {
                    const dept = departmentWithOrganisation.department;
                    this.selectedDepartment = _.cloneDeep(dept);
                  }
                }

                if (
                  job.customerInternalInformation?.[this.loggedInCustomer!._id]
                ) {
                  this.customerInternalInformation =
                    job.customerInternalInformation[this.loggedInCustomer!._id];
                  this.demandReasonId =
                    this.customerInternalInformation.demandReason?.id;
                }
                this.mergeCustomerCustomCriteriaIntoJobInternalInfo();
                this.cancelUser = Users.findOne({
                  _id: job.canceledBy,
                });
                const transferForUser = this.job.transfers?.find(
                  (transfer) =>
                    transfer.targetCustomer.id === this.loggedInCustomer?._id,
                );
                this.transferredToAll =
                  (this.job.transfers?.every(
                    (transfer) => transfer.transferredAt,
                  ) &&
                    this.job.transfers?.length > 0) ||
                  false;
                this.transferConfirmed =
                  !!this.job.transferConfirmation?.confirmedAt;
                this.selectedTransferConfirmationPerson =
                  this.job.transferConfirmation?.confirmedPerson;
                this.hasTransferred =
                  this.job.transfers?.some(
                    (transfer) => transfer.transferredAt,
                  ) || false;
                this.hasSharedPoolInvitees = this.job.invitees.some(
                  (invitee) =>
                    invitee.customer?.id !== this.loggedInCustomer?._id,
                );

                if (this.hasTransferred || this.hasSharedPoolInvitees) {
                  this.job.allowExternalInvitees = true;
                  this.hasToggledExternalInvitees = true;
                }

                if (this.job.allowExternalInvitees === undefined) {
                  this.job.allowExternalInvitees =
                    !this.loggedInCustomer?.externalPoolerOffByDefault;
                }
                const externalDraftTransfer = this.job.transfers?.find(
                  (transfer) => {
                    return (
                      transfer.externalStillDraft &&
                      transfer.secret &&
                      transfer.transferredAt
                    );
                  },
                );
                if (externalDraftTransfer) {
                  Meteor.call(
                    'checkIfJobIsDraft',
                    jobId,
                    externalDraftTransfer.targetCustomer.id,
                    (err: any, res: any) => {
                      if (err) {
                        console.log('ERROR: ', err);
                      }
                      if (
                        !res &&
                        this.job.transfers &&
                        this.job.transfers.indexOf(externalDraftTransfer)
                      ) {
                        this.job.transfers[
                          this.job.transfers.indexOf(externalDraftTransfer)
                        ].externalStillDraft = false;
                      }
                    },
                  );
                }
                this.suggestInsteadDirectAccept =
                  transferForUser && transferForUser.preventDirectAccept;

                this.isArchivedJob = job.status === JobStatusEnum.ARCHIVED;
                this.getInvitees();

                if (this.loggedInCustomer?.enableTags) {
                  const organisation = Organisations.findOne({
                    'departments.id': this.job.department.id,
                  });
                  this.organisationTags =
                    organisation?.customerInformation?.[
                      this.loggedInCustomer._id
                    ]?.tags || [];
                }
                if (this.user?.branch) {
                  this.job.branch = this.job.branch
                    ? this.job.branch
                    : this.user.branch;
                }
                if (job.customer.id != this.loggedInCustomer?._id) {
                  let transferIndex = job.transfers?.findIndex(
                    (transfer) =>
                      transfer.targetCustomer.id == this.loggedInCustomer?._id,
                  );
                  if (
                    transferIndex !== undefined &&
                    transferIndex > -1 &&
                    job.transfers?.[transferIndex] &&
                    this.user
                  ) {
                    this.transferToMe = job.transfers[transferIndex];
                    this.branch =
                      job.transfers[transferIndex].targetCustomer.branchId ||
                      this.user.branch;
                    this.editor =
                      job.transfers[transferIndex].targetCustomer.editorId ||
                      this.user._id;
                  }
                } else {
                  this.branch = job.branch;
                  this.editor = job.createdBy.userId;
                }
                if (this.job?.department) {
                  this.selectedDepartmentId = this.job.department.id;
                }

                setConditionalTransfersForJob({
                  job: this.job,
                  customer: this.loggedInCustomer,
                });
                this.filterUsers().then();
              });
            }
          });
        },
      );
    } else {
      this.getNewJob();
    }

    const usersObserver = Users.find({ role: RoleEnum.POOL }).observe({
      changed: () => {
        this.filterUsers().then();
      },
    });
    this.meteorLiveQueryHandlers.push(usersObserver);

    const jobsObserver = Jobs.find({ _id: jobId }).observe({
      changed: () => {
        if (this.jobUsersSubscription) {
          this.jobUsersSubscription.unsubscribe();
        }
        this.jobUsersSubscription = MeteorObservable.subscribe(
          'usersOfJob',
          jobId,
        ).subscribe();
      },
    });
    this.meteorLiveQueryHandlers.push(jobsObserver);
  }

  async subscribeJobData(jobId: string) {
    return new Promise<void>((resolve) => {
      this.jobUsersSubscription?.unsubscribe();
      this.jobUsersSubscription = MeteorObservable.subscribe(
        'usersOfJob',
        jobId,
      ).subscribe(() => {
        resolve();
      });
    });
  }

  trackByTransferTarget(index: number, transfer: Transfer): string {
    return transfer.targetCustomer.id;
  }

  trackByShift(index: number, shift: Shift): string {
    return shift.shift.toString();
  }

  async applyQueryParamsToJob() {
    this.finishedLoadingQueryParams = false;
    const paramMap = this.route.snapshot.queryParamMap;
    const multiDaySelectionParam = paramMap.get('multiDaySelection');
    const allowExternalInviteesParam = paramMap.get('allowExternalInvitees');

    const paramDepartmentId = paramMap.get('departmentId');

    if (allowExternalInviteesParam === 'true') {
      this.job.allowExternalInvitees = true;
      this.hasToggledExternalInvitees = true;
    }

    // certificates
    this.paramCertificates = paramMap.get('certificates')?.split(',') || [];
    const certificates = Certificates.find({
      _id: { $in: this.paramCertificates },
    }).fetch();
    if (certificates.length) {
      const mappedCertificates: Job['certificates'] = certificates
        .filter((i) => !!i)
        .map((i) => ({
          id: i._id,
          displayName: i.name,
        }));
      this.job.certificates = mappedCertificates;
    }

    // specialities
    this.paramSpecialities = paramMap.get('specialities')?.split(',') || [];
    const specialities = Specialities.find({
      _id: { $in: this.paramSpecialities },
    }).fetch();
    if (specialities.length) {
      const mappedSpecialities: Job['specialities'] = specialities
        .filter((i) => !!i)
        .map((i) => ({
          id: i._id,
          displayName: i.name,
        }));
      this.job.specialities = mappedSpecialities;
    }

    // department
    if (!paramDepartmentId) {
      this.finishedLoadingQueryParams = true;
      return;
    }
    const selectedDepartment = (
      await this.organisationService.getOrganisationAndDepartmentById(
        paramDepartmentId,
      )
    )?.department;

    if (!selectedDepartment) {
      this.finishedLoadingQueryParams = true;
      return;
    }
    // date shift & user selection
    if (multiDaySelectionParam) {
      this.hasParamUsers = true;
      const multiDaySelection = urlDecodeMultiDaySelection(
        multiDaySelectionParam,
      );
      const hasMultipleJobs =
        multiDaySelection.days.length > 1 ||
        multiDaySelection.days?.[0]?.shifts.length > 1;
      if (hasMultipleJobs) {
        await this.applyMultiDaySelection(
          multiDaySelection,
          selectedDepartment,
        );
      } else {
        // apply multiDay selection with just one job
        const day = multiDaySelection.days[0];
        const shift = day.shifts[0];
        const { inviteUserIds, bookUserIds } = shift;
        await this.applySingleDaySelection({
          date: day.date,
          shiftId: shift.id,
          inviteUserIds,
          bookUserIds,
          selectedDepartment,
        });
      }
    }
    this.finishedLoadingQueryParams = true;
  }

  async applySingleDaySelection({
    date,
    shiftId,
    bookUserIds,
    inviteUserIds,
    selectedDepartment,
  }: {
    date: string;
    shiftId: string;
    bookUserIds: string[];
    inviteUserIds: string[];
    selectedDepartment: Department;
  }) {
    // date
    if (date) {
      this.job.date = date;
      this.dateRanges = [{ from: addTimeZoneOffset(new Date(this.job.date)) }];
      await this.dateRangesChanged();
    }

    await this.selectDepartment(selectedDepartment);
    // shift
    if (shiftId) {
      const desiredShift = this.selectedDepartment?.shifts.find(
        (shift) => shift.id === shiftId,
      );
      if (!desiredShift) {
        return;
      }
      this.job.shifts = [desiredShift];

      this.job.jobType = desiredShift.specificJobTypes?.[0] || this.job.jobType;
      this.jobSource$.next(this.job);
      this.selectShift();
      this._setTransfers(this.job);
      this._setArchiveAt(this.job);
    }

    // users
    if (!shiftId || !date) {
      // there will be no users if there is no shift or date
      return;
    }
    await firstValueFrom(this.usersReady$.pipe(filter((ready) => ready)));

    this.ngZone.run(() => {
      if (bookUserIds) {
        this.directAcceptUsers = this.possibleUsers.filter((user) =>
          bookUserIds.includes(user._id),
        );
        if (bookUserIds.length > 1) {
          this.job.requiredUsers = bookUserIds.length;
        }
      }
      if (inviteUserIds.length) {
        this.selectedUsers = this.possibleUsers.filter((user) =>
          inviteUserIds.includes(user._id),
        );
      }
      this.form?.control.markAsDirty();
      this.updateCachedSelection();
    });
  }

  async applyMultiDaySelection(
    selection: DecodedMultiDaySelection,
    selectedDepartment: Department,
  ) {
    this.dateRanges = selection.days.flatMap((day) =>
      day.shifts.map((shift) => ({
        // select the same day multiple times for different shifts
        from: startOfDay(new Date(day.date)),
      })),
    );
    await this.selectDepartment(selectedDepartment);
    await this.dateRangesChanged();
    const firstShiftUuid = selection.days[0].shifts[0].id;
    const desiredShift = this.selectedDepartment?.shifts.find(
      (shift) => shift.id === firstShiftUuid,
    );
    if (!desiredShift) {
      this.finishedLoadingQueryParams = true;
      return;
    }
    this.job.shifts = [desiredShift];
    this.job.jobType = desiredShift.specificJobTypes?.[0] || this.job.jobType;
    this.jobSource$.next(this.job);
    // now that days are selected and possible users fetched, we want to apply the actual selection
    let totalIndex = 0;
    for (let i = 0; i < selection.days.length; i++) {
      const day = selection.days[i];

      for (let j = 0; j < day.shifts.length; j++) {
        const selectionShift = day.shifts[j];
        const multiDay = this.multiDays[totalIndex];
        const shift = this.selectedDepartment?.shifts.find(
          (shift) => shift.id === selectionShift.id,
        );
        multiDay.jobType = shift?.specificJobTypes?.[0] || JobTypeEnum.NORMAL;

        if (!shift) throw new Error('Shift not found for multiDaySelection');
        multiDay.shifts = [shift];
        // filter by the new shift
        await this.updateUsersForMultiDay(multiDay);
        // select the users
        multiDay.inviteUsers = multiDay.possibleUsers.filter((possibleUser) =>
          selectionShift.inviteUserIds.includes(possibleUser._id),
        );
        multiDay.directAcceptUsers = multiDay.possibleUsers.filter(
          (possibleUser) =>
            selectionShift.bookUserIds.includes(possibleUser._id),
        );
        // if there is book & invite set required users to bookedUsers + 1
        // by default and if it is 0 set it to 1 with "|| 1"
        multiDay.requiredUsers =
          multiDay.directAcceptUsers.length +
            (multiDay.inviteUsers.length ? 1 : 0) || 1;
        totalIndex++;
      }
    }
    this._setTransfers(this.job);

    this.multiDayInviteUsers = 0;
    this.multiDayDirectAcceptUsers = 0;
    this.multiDays.forEach((multiDay) => {
      this.multiDayInviteUsers += multiDay.inviteUsers.length;
      this.multiDayDirectAcceptUsers += multiDay.directAcceptUsers.length;
    });

    this.checkRestViolations();

    this.form?.control.markAsDirty();
    this.updateCachedSelection();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.jobUsersSubscription) {
      this.jobUsersSubscription.unsubscribe();
    }
    if (this.jobSubscription) {
      this.jobSubscription.unsubscribe();
    }
    if (this.usersTracker) {
      this.usersTracker.stop();
    }
    if (
      this.meteorLiveQueryHandlers &&
      this.meteorLiveQueryHandlers.length > 0
    ) {
      this.meteorLiveQueryHandlers.forEach((liveQueryHandler) => {
        liveQueryHandler.stop();
      });
    }
    if (this.userTracker) {
      this.userTracker.stop();
    }
    if (this.jobTracker) {
      this.jobTracker.stop();
    }
  }

  selectShift() {
    // this triggers change detection on child components taking in shifts
    this.job.shifts = [...this.job.shifts];
    this.filterUsers().then();
    if (this.job.shifts && this.job.shifts.length > 0) {
      this._setTransfers(this.job);
      this._setArchiveAt(this.job);
      this._updateMultiDays().then();
      this.evaluateJobIsInPast();
    }
    this.form?.control.markAsDirty();
  }

  certificatesSelected(certificates: Certificate[]) {
    if (!this.isNewJob && !this._isOwnJob() && !this.transferToMe) {
      return;
    }
    if (this._isOwnJob()) {
      this.job.certificates = certificates.map((certificates) => {
        return { id: certificates._id, displayName: certificates.name };
      });
      setConditionalTransfersForJob({
        job: this.job,
        customer: this.loggedInCustomer,
      });
    } else {
      if (!this.transferToMe) {
        return;
      }
      if (!this.transferToMe.criteria) {
        this.transferToMe.criteria = {};
      }
      this.transferToMe.criteria.certificates = certificates.map(
        (certificates) => {
          return { id: certificates._id, displayName: certificates.name };
        },
      );
      const fullCertificates = [
        ...(this.transferToMe.criteria.certificates?.map((c) => c.id) || []),
        ...(this.job.certificates?.map((c) => c.id) || []),
      ];
      this.certificateSet = new Set(fullCertificates);
      this.certificatesString =
        this.job.certificates?.map((c) => c.displayName).join(', ') || '';
    }

    this.filterUsers().then();
    this.form?.control.markAsDirty();
  }

  specialitiesSelected(specialities: Speciality[]) {
    if (!this.isNewJob && !this._isOwnJob() && !this.transferToMe) {
      return;
    }
    if (this._isOwnJob()) {
      this.job.specialities = specialities.map((speciality) => {
        return { id: speciality._id, displayName: speciality.name };
      });
    } else {
      if (!this.transferToMe) {
        return;
      }
      if (!this.transferToMe.criteria) {
        this.transferToMe.criteria = {};
      }
      this.transferToMe.criteria.specialities = specialities.map(
        (speciality) => {
          return { id: speciality._id, displayName: speciality.name };
        },
      );
      const fullSpecialties = [
        ...(this.transferToMe.criteria.specialities?.map((s) => s.id) || []),
        ...(this.job.specialities?.map((s) => s.id) || []),
      ];
      this.specialitySet = new Set(fullSpecialties);
      this.specialityString =
        this.job.specialities?.map((s) => s.displayName).join(', ') || '';
    }
    this.filterUsers().then();
    this.form?.control.markAsDirty();
  }

  async selectDepartment(department?: Department, manualChange = false) {
    const oldDepartment = this.selectedDepartment;
    if (!department) {
      this._resetDepartment();

      let oldDefaultValues: AssignmentDefault | undefined =
        this.loggedInCustomer?.defaultJobValues?.find(
          (defaultJobValue) =>
            defaultJobValue.department.id === oldDepartment?.id,
        );
      if (oldDefaultValues) {
        if (this.job.description === oldDefaultValues.values?.description) {
          this.job.description = '';
        }
        if (
          this.job.certificates?.every((certificate) =>
            oldDefaultValues?.values?.certificates?.some(
              (oldDefaultCertificate) =>
                oldDefaultCertificate.id === certificate.id,
            ),
          ) &&
          !this.paramCertificates.length
        ) {
          this.job.certificates = [];
        }
        if (
          this.job.specialities?.every((speciality) =>
            oldDefaultValues?.values?.specialities?.some(
              (oldDefaultSpeciality) =>
                oldDefaultSpeciality.id === speciality.id,
            ),
          ) &&
          !this.paramSpecialities.length
        ) {
          this.job.specialities = [];
        }
        if (
          this.job.poolerTypes?.every((poolerType) =>
            oldDefaultValues?.values?.poolerTypes?.some(
              (oldDefaultPoolerType) =>
                oldDefaultPoolerType.id === poolerType.id,
            ),
          )
        ) {
          this.job.poolerTypes = [];
        }
      }
      return;
    }
    await this.organisationService
      .getOrganisationAndDepartmentById(department.id)
      .then((result) => {
        if (!result) {
          return;
        }
        if (result.department) {
          this.clearInviteeSelection();
          this.selectedDepartment = {
            ...result.department,
            shifts: result.department.shifts.map((shift) => ({ ...shift })),
          };
          this.job.shifts = [];
          this.job.department = {
            id: department.id,
            name: result.department.name,
            phone: result.department.phone,
            costCenter: result.department.costCenter,
            clinic: result.department.clinic,
            section: result.department.section,
            organisationalUnit: result.department.organisationalUnit,
          };
          this.job.organisation = {
            id: result.organisation._id,
            name: result.organisation.name,
            address: result.organisation.address,
          };
          if (this.loggedInCustomer?.enableTags) {
            this.organisationTags =
              result.organisation?.customerInformation?.[
                this.loggedInCustomer._id
              ]?.tags || [];
          }

          let oldDefaultValues: AssignmentDefault | undefined =
            this.loggedInCustomer?.defaultJobValues?.find(
              (defaultJobValue) =>
                defaultJobValue.department.id === oldDepartment?.id,
            );
          if (oldDefaultValues) {
            if (this.job.description === oldDefaultValues.values?.description) {
              this.job.description = '';
            }
            if (
              this.job.certificates?.every((certificate) =>
                oldDefaultValues?.values?.certificates?.some(
                  (oldDefaultCertificate) =>
                    oldDefaultCertificate.id === certificate.id,
                ),
              ) &&
              !this.paramCertificates.length
            ) {
              this.job.certificates = [];
            }
            if (
              this.job.specialities?.every((speciality) =>
                oldDefaultValues?.values?.specialities?.some(
                  (oldDefaultSpeciality) =>
                    oldDefaultSpeciality.id === speciality.id,
                ),
              ) &&
              !this.paramSpecialities.length
            ) {
              this.job.specialities = [];
            }
          }

          let defaultValues: AssignmentDefault | undefined =
            this.loggedInCustomer?.defaultJobValues?.find(
              (defaultJobValue) =>
                defaultJobValue.department.id === department.id,
            );
          this.ngZone.run(() => {
            if (defaultValues) {
              if (defaultValues.values?.description) {
                this.job.description = defaultValues.values.description;
              }
              if (
                defaultValues.values?.certificates &&
                !this.paramCertificates.length &&
                !this.hasParamUsers
              ) {
                this.job.certificates = defaultValues.values.certificates;
              }
              if (
                defaultValues.values?.specialities &&
                !this.paramSpecialities.length &&
                !this.hasParamUsers
              ) {
                this.job.specialities = defaultValues.values.specialities;
              }
              if (defaultValues.values?.poolerTypes && !this.hasParamUsers) {
                this.job.poolerTypes = defaultValues.values.poolerTypes;
              }
            }
          });

          this.selectedDepartmentId = result.department.id;
          this.jobSource$.next(this.job);
        }
      });

    if (manualChange) {
      this._updateMultiDays().then();
    }
  }

  async dateRangesChanged(newDateRanges?: { dateRanges: DateRange[] }) {
    this.dateRanges = sortDateRanges(
      newDateRanges?.dateRanges || this.dateRanges,
    );
    const dates = getDatesFromRanges(this.dateRanges);
    this.updateSelectedWeekDays(dates);
    this.multiDay = dates.length > 1;
    if (this.multiDay) {
      await this._updateMultiDays();
    } else {
      const date = this.dateRanges[0].from;
      if (!date) {
        return;
      }
      this.job.date = format(date, dbDayDateToken);
      this.filterUsers().then();
      this._setTransfers(this.job);
      this._setArchiveAt(this.job);
    }
    this.evaluateJobIsInPast();
  }

  updateSelectedWeekDays(dates: Date[]) {
    this.selectedWeekDays = _.uniq(
      dates.map((date) => getWeekDayEnumByIndex(getDay(date))),
    );
  }

  transferAutoCheckboxChange(index: number) {
    if (!this.job.transfers?.[index] || !this.user) {
      return;
    }
    this.job.transfers[index].auto = !this.job.transfers[index].auto;
    this.job.transfers[index].transferredBy = {
      userId: this.user._id,
      firstName: this.user.firstName,
      lastName: this.user.lastName,
    };
    this.form?.control.markAsDirty();
  }

  directTransferCheckboxChange(transfer: Transfer) {
    if (!this.user) return;

    if (!transfer.directTransfer) {
      this.oldTransferIsAutoByCustomerId.set(
        transfer.targetCustomer.id,
        transfer.auto,
      );
    }
    transfer.directTransfer = !transfer.directTransfer;
    transfer.auto = transfer.directTransfer
      ? false
      : this.oldTransferIsAutoByCustomerId.get(transfer.targetCustomer.id) ||
        false;
    transfer.transferredBy = {
      userId: this.user._id,
      firstName: this.user.firstName,
      lastName: this.user.lastName,
    };
    this.transferAll =
      this.job?.transfers?.every((transfer) => transfer.directTransfer) ||
      false;
    this.form?.control.markAsDirty();
  }

  directTransferAllCheckboxChange() {
    if (!this.user) return;
    this.transferAll = !this.transferAll;
    this.job?.transfers?.forEach((transfer) => {
      if (!transfer.directTransfer) {
        this.oldTransferIsAutoByCustomerId.set(
          transfer.targetCustomer.id,
          transfer.auto,
        );
      }
      transfer.directTransfer = this.transferAll;
      transfer.auto = transfer.directTransfer
        ? false
        : this.oldTransferIsAutoByCustomerId.get(transfer.targetCustomer.id) ||
          false;
      transfer.transferredBy = {
        userId: this.user!._id,
        firstName: this.user!.firstName,
        lastName: this.user!.lastName,
      };
    });
    this.form?.control.markAsDirty();
  }

  transferPreventDirectAcceptCheckboxChange(index: number) {
    if (!this.job.transfers?.[index]) {
      return;
    }
    this.job.transfers[index].preventDirectAccept =
      !this.job.transfers[index].preventDirectAccept;
    this.form?.control.markAsDirty();
  }

  selectLanguage(value: LanguageEnum) {
    if (
      value !== this.job.language ||
      (!this._isOwnJob() && value !== this.transferToMe?.criteria?.language)
    ) {
      if (!this._isOwnJob() && this.transferToMe) {
        if (!this.transferToMe.criteria) {
          this.transferToMe.criteria = {};
        }
        this.transferToMe.criteria.language = value;
      } else {
        this.job.language = value;
      }
      this.filterUsers().then();
    }
  }

  poolerTypesSelected(poolerTypeIds: string[]) {
    const poolerTypes = this.poolerTypes.filter((poolerType) =>
      poolerTypeIds.includes(poolerType.id),
    );
    if (!this.isNewJob && !this._isOwnJob() && !this.transferToMe) {
      return;
    }
    if (this._isOwnJob()) {
      this.job.poolerTypes = poolerTypes;
      setConditionalTransfersForJob({
        job: this.job,
        customer: this.loggedInCustomer,
      });
    } else {
      if (!this.transferToMe) {
        return;
      }
      if (!this.transferToMe.criteria) {
        this.transferToMe.criteria = {};
      }
      this.transferToMe.criteria.poolerTypes = poolerTypes;
    }
    this.filterUsers().then();
    this.form?.control.markAsDirty();
  }

  selectSex(value: SexEnum) {
    if (
      value !== this.job.sex ||
      (!this._isOwnJob() && value !== this.transferToMe?.criteria?.sex)
    ) {
      if (!this._isOwnJob() && this.transferToMe) {
        if (!this.transferToMe.criteria) {
          this.transferToMe.criteria = {};
        }
        this.transferToMe.criteria.sex = value;
      } else {
        this.job.sex = value;
      }
      this.filterUsers().then();
    }
  }

  selectJobType(value: JobTypeEnum) {
    if (value !== this.job.jobType && this._isOwnJob()) {
      this.job.jobType = value;
      this.jobSource$.next(this.job);

      const shiftsAreCompatible = this.job.shifts?.every(
        (shift) =>
          !shift.specificJobTypes ||
          shift.specificJobTypes?.includes(this.job.jobType),
      );
      if (!shiftsAreCompatible) {
        // we completely discard the shifts so that the user must notice our change by the missing required fields
        this.job.shifts = [];
      }
      if (this.multiDay) {
        this.modifyMultiDaysJobType(value);
      } else {
        this.job.jobType = value;
        this.jobSource$.next(this.job);
        this.filterUsers().then();
      }
    }
  }

  selectMobility(value: CarLicenseEnum) {
    if (
      value !== this.job.car ||
      (!this._isOwnJob() && value !== this.transferToMe?.criteria?.car)
    ) {
      if (!this._isOwnJob() && this.transferToMe) {
        if (!this.transferToMe.criteria) {
          this.transferToMe.criteria = {};
        }
        this.transferToMe.criteria.car = value;
      } else {
        this.job.car = value;
      }
      this.filterUsers().then();
    }
  }

  evaluateJobIsInPast() {
    if (!this.multiDay && this.job.shifts?.[0]?.id) {
      this.ngZone.run(() => {
        this.jobInPast = getTimeSinceJobFinished(this.job) < 0;
      });
    }
  }

  async filterUsers(skipClear?: boolean) {
    this.violationMessagesPerDayUuid = new Map();
    const poolerMap = await firstValueFrom(
      this.poolerService.readyPoolerCustomerMap$,
    );
    this.syncCustomerInternalInformationToJob();
    if (!skipClear) {
      this.clearInviteeSelection();
    }
    if (this.multiDay) {
      if (
        !(
          this.dateRanges.length &&
          this.job.department &&
          this.job.shifts &&
          this.job.shifts.length > 0
        )
      ) {
        return;
      }
      if (this.usersTracker) {
        this.usersTracker.stop();
      }
      this.usersTracker = Tracker.autorun(() => {
        this.ngZone.run(() => {
          this.filterMultiDayUsers();
        });
      });
    } else {
      if (
        !(
          this.job.date &&
          this.job.department &&
          this.job.shifts &&
          this.job.shifts.length > 0
        )
      ) {
        this.possibleUsers = [];
        return;
      }
      this.loadingUsers = true;
      if (this.usersTracker) {
        this.usersTracker.stop();
      }
      this.usersTracker = Tracker.autorun(() => {
        this.ngZone.run(() => {
          this.possibleUsers = [
            ...getPossibleUsersForJob({
              date: this.job.date,
              job: this.job,
              shifts: this.job.shifts,
              department: this.selectedDepartment!,
              loggedInCustomer: this.loggedInCustomer!,
              sharedPoolCustomers: this.sharedPoolCustomers,
              poolerMap: poolerMap,
              user: this.user!,
              i18n: this.i18n!,
              isOwnJob: this._isOwnJob(),
              undecided: this.undecided,
              ignoreUserPreferenceDepartmentBlacklist:
                this.ignoreUserPreferenceDepartmentBlacklist,
              jobInPast: this.jobInPast,
              search: this.userSearch,
              transferToMe: this.transferToMe,
            }),
          ];

          if (this.loggedInCustomer?.externalPoolerOffByDefault) {
            let totalUserCount = 0;
            if (!this.possibleUsers.length) {
              totalUserCount = Users.find().fetch().length;
            }
            let userCountWithoutUndecided = this.possibleUsers.filter(
              (user) =>
                !user.isUndecided &&
                user.sourceCustomer?.id === this.loggedInCustomer?._id,
            ).length;
            if (this.undecidedTimeout) clearTimeout(this.undecidedTimeout);
            if (
              this.undecided &&
              !this.hasToggledUndecided &&
              userCountWithoutUndecided
            ) {
              this.undecided = false;
              this.filterUsers();
              return;
            } else if (
              !this.possibleUsers.length &&
              !this.hasToggledUndecided &&
              !this.undecided &&
              totalUserCount > 1
            ) {
              this.undecidedTimeout = setTimeout(() => {
                this.undecided = true;
                this.filterUsers();
              }, 600);
              return;
            }
          }
          if (!this.usersReady$.value && this.possibleUsers.length) {
            this.usersReady$.next(true);
          }
          this.loadingUsers = false;
          this.applyCachedSelectionForSingleDay();
        });
      });
    }
  }

  clearInviteeSelection() {
    if (!this.finishedLoadingQueryParams) {
      return;
    }
    this.clearDayInviteeSelection();
  }

  multiDayInviteeSelectionToggled(multiDay: MultiDay, selected: boolean) {
    if (selected) {
      this.selectAllMultiDayInvitees(multiDay);
    } else {
      this.clearAllAMultiDayInvitees(multiDay);
    }
  }

  inviteeSelectionToggled(selected: boolean) {
    if (selected) {
      this.selectAllDayInvitees();
    } else {
      this.clearDayInviteeSelection();
    }
    this.updateCachedSelection();
  }

  selectAllDayInvitees() {
    this.selectedUsers = this.possibleUsers.filter(
      (u) =>
        !u.restWarning && !u.availabilityNote && !u.hasBlacklistedJobDepartment,
    );
    this.directAcceptUsers = [];
  }

  clearDayInviteeSelection() {
    this.selectedUsers = [];
    this.directAcceptUsers = [];
  }

  private resetFormStateAfterSave() {
    // Mark form as pristine to prevent unsaved changes guard from triggering
    this.form?.control.markAsPristine();
    // Clear selected users
    this.selectedUsers = [];
    this.directAcceptUsers = [];
    // Clear multi-day selections
    this.multiDays.forEach((day) => {
      day.inviteUsers = [];
      day.directAcceptUsers = [];
    });
    this.multiDayInviteUsers = 0;
    this.multiDayDirectAcceptUsers = 0;
  }

  selectAllInviteesForAllMultiDays() {
    this.multiDays.forEach((multiDay) =>
      this.selectAllMultiDayInvitees(multiDay),
    );
  }

  clearAllInviteesForAllMultiDays() {
    this.multiDays.forEach((multiDay) =>
      this.clearAllAMultiDayInvitees(multiDay),
    );
  }

  selectAllMultiDayInvitees(multiDay: MultiDay) {
    multiDay.inviteUsers = multiDay.possibleUsers.filter(
      // Don't select users with warnings automatically
      (u) =>
        !u.restWarning && !u.availabilityNote && !u.hasBlacklistedJobDepartment,
    );
    multiDay.directAcceptUsers = [];
  }

  clearAllAMultiDayInvitees(multiDay: MultiDay) {
    multiDay.inviteUsers = [];
    multiDay.directAcceptUsers = [];
  }

  historyBack() {
    this.location.back();
  }

  updatePossibleUsersSearch() {
    this.filterUsers(true).then();
  }

  submitJob(historyBack = true) {
    //  Check if there are 50 or more new invitees and if so show confirmation dialog
    if (this.selectedUsers?.length > 50) {
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        data: {
          id: 'inviteManyPeopleConfirmation' + (this.job._id || 'new'),
          text:
            this.i18n?.confirmManyInvites ||
            'Möchten Sie wirklich über 50 Einladungen los schicken',
        },
      });
      dialogRef.afterClosed().subscribe((result: string) => {
        if (result) {
          this.saveJob(historyBack);
        }
      });
    } else {
      this.saveJob(historyBack);
    }
  }

  async saveJob(
    historyBack = true,
    options?: {
      restViolationIgnored?: boolean;
    },
  ) {
    this.submitted = true;
    if (
      !this.form?.valid ||
      !this.dateRanges?.filter((range) => range.from)?.length ||
      (!this.multiDay && !this.job.date) ||
      (!this.selectedDepartment && this.isNewJob) ||
      (this.multiDay && !this.enabledJobs) ||
      !this.job.shifts?.length ||
      !this.shiftsValid ||
      !this.job.requiredUsers ||
      (this.loggedInCustomer?.requiredJobFields?.certificates &&
        !this.job.certificates?.length) ||
      (this.loggedInCustomer?.requiredJobFields?.demandReason &&
        !this.customerInternalInformation.demandReason)
    ) {
      this.form?.control.markAsDirty();
      this.form?.control.markAllAsTouched();
      this.toastService.add({ type: 'INFO', message: 'InvalidForm' });
      return;
    }
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    if (
      this.job.certificates?.some((certificate) => !certificate.displayName)
    ) {
      this.job.certificates = this.job.certificates.map((certificate) => ({
        ...certificate,
        displayName: Certificates.findOne(certificate.id)?.name,
      }));
    }
    if (!this.multiDay) {
      // Check for booking warnings on direct accept users before saving
      if (!this.loggedInCustomer) {
        this.isSaving = false;
        return;
      }
      const loggedInCustomerId = this.loggedInCustomer._id;
      const allConfirmed = await processBookingWarnings(
        this.directAcceptUsers.map((u) => ({
          user: u,
          poolerType:
            u.customerInformation?.[u.sourceCustomer?.id || loggedInCustomerId]
              ?.poolerType,
        })),
        this.dialog,
        this.i18n,
      );

      if (!allConfirmed) {
        this.isSaving = false;
        return;
      }

      let oldInvitees = [...this.job.invitees];
      this.job = this.jobService.updateInvitees(
        this.job,
        this.selectedUsers,
        this.directAcceptUsers,
        this.loggedInCustomer!,
        this.sharedPoolCustomers,
        this.suggestInsteadDirectAccept,
      );
      this.jobSource$.next(this.job);
      const bookedInviteeCount =
        this.job.invitees?.filter(
          (invitee) => invitee.status === InviteeStatusEnum.BOOKED,
        )?.length || 0;
      if (bookedInviteeCount > this.job.requiredUsers) {
        this.job.invitees = oldInvitees;
        this.isSaving = false;
        this.form?.control.markAsDirty();
        this.form?.control.markAllAsTouched();
        this.toastService.add({
          type: 'ERROR',
          message: 'requiredUsersBelowBookedCount',
        });
        return;
      }
    }
    if (!this.isNewJob) {
      try {
        await this.jobService.updateJob(
          this.job,
          this.customerInternalInformation,
          this.loggedInCustomer!,
          this.user!,
          this.branch,
          true,
        );

        // Reset form state after successful save to prevent unsaved changes guard from triggering
        this.resetFormStateAfterSave();
        if (historyBack) {
          this.toastService.add({ type: 'SUCCESS', message: 'jobSaved' });
          this.historyBack();
        }
      } catch (error) {
        this.toastService.add({
          type: 'ERROR',
          message: (error as Meteor.Error).error?.toString() || 'serverError',
        });
        this.isSaving = false;
      }
    } else {
      if (this.loggedInCustomer) {
        let job: Job;
        job = await this._updateJob(this.job);
        if (this.multiDay) {
          if (this.violationMessagesPerDayUuid) {
            const violationMessages = Array.from(
              this.violationMessagesPerDayUuid.entries(),
            ).map(([_, messages]) => messages);
            const overlaps = violationMessages
              .flatMap((messages) => messages.overlaps)
              .filter(filterIsNotEmpty);
            if (overlaps.length) {
              const overlappingNames = overlaps.map(
                (o) => o.user.firstName + ' ' + o.user.lastName,
              );
              const uniqueNames = Array.from(new Set(overlappingNames));
              this.toastService.add({
                type: 'ERROR',
                message: 'jobOverlapsDetectedForNames',
                messageOptions: {
                  names: uniqueNames.join(', '),
                },
                timeout: 10000,
              });
              this.isSaving = false;
              return;
            }
            const restViolationMessages = Array.from(
              this.violationMessagesPerDayUuid.values(),
            )
              .map((messages) => messages.restViolationsMessage)
              .filter(filterIsNotEmpty);
            if (
              restViolationMessages.length &&
              !options?.restViolationIgnored
            ) {
              const dialogRef = this.dialog.open(ConfirmationDialog, {
                data: {
                  id: randomId(),
                  title: this.i18n?.restViolationsDetected,
                  text: replaceTranslationVariables(
                    this.i18n?.confirmIgnoreRestViolations,
                    { violations: restViolationMessages.join('\n\n') },
                  ),
                },
              });
              dialogRef.afterClosed().subscribe((result: boolean) => {
                if (result) {
                  this.saveJob(historyBack, { restViolationIgnored: true });
                }
              });
              this.isSaving = false;
              return;
            }
          }
          // Check for booking warnings on multiDay direct accept users before saving
          if (!this.loggedInCustomer) {
            this.isSaving = false;
            return;
          }

          const enabledMultiDays = this.multiDays.filter((day) => day.enabled);
          const allMultiDayDirectAcceptUsers: JobMatchPoolerUser[] = [];

          for (const singleDay of enabledMultiDays) {
            allMultiDayDirectAcceptUsers.push(...singleDay.directAcceptUsers);
          }

          // Get sourceCustomerId from first user if available (assuming all users have same sourceCustomer)
          const loggedInCustomerId = this.loggedInCustomer._id;
          const allConfirmed = await processBookingWarnings(
            allMultiDayDirectAcceptUsers.map((u) => ({
              user: u,
              poolerType:
                u.customerInformation?.[
                  u.sourceCustomer?.id || loggedInCustomerId
                ]?.poolerType,
            })),
            this.dialog,
            this.i18n,
          );

          if (!allConfirmed) {
            this.isSaving = false;
            return;
          }

          job.multiDayId = randomId();
          Promise.all(
            enabledMultiDays.map(async (singleDay) => {
              job.date = singleDay.date;
              job.shifts = singleDay.shifts;
              job.requiredUsers = singleDay.requiredUsers;
              job.jobType = singleDay.jobType;
              job.invitees = [
                ...singleDay.directAcceptUsers.map((user) =>
                  this.jobService.createInviteeForUser(
                    user,
                    'DIRECT_ACCEPT',
                    this.loggedInCustomer!,
                    this.sharedPoolCustomers,
                    this.suggestInsteadDirectAccept,
                  ),
                ),
                ...singleDay.inviteUsers.map((user) =>
                  this.jobService.createInviteeForUser(
                    user,
                    'INVITE',
                    this.loggedInCustomer!,
                    this.sharedPoolCustomers,
                    this.suggestInsteadDirectAccept,
                  ),
                ),
              ];
              job.status = JobStatusEnum.OPEN;
              job.jobStart = getJobStart(job.shifts, singleDay.date);
              this._setArchiveAt(job);
              job.transfers = this._setMultiDayTransfers(job);
              return this.jobService.createJob(job, allConfirmed, false);
            }),
          )
            .catch((error) => {
              console.error(error);
              this.toastService.add({
                type: 'ERROR',
                message: 'multiJobInsertError',
              });
              this.isSaving = false;
            })
            .then((result) => {
              if (typeof result === 'object') {
                const successfulJobs = result.filter(
                  (res) => typeof res === 'object',
                );
                this.toastService.add({
                  type: 'SUCCESS',
                  message: 'xNewJobsCreated',
                  messageOptions: { count: successfulJobs.length.toString() },
                });
                this.resetFormStateAfterSave();
                this.historyBack();
              }
            });
        } else {
          if (!job.date) {
            return;
          }
          job.status = JobStatusEnum.OPEN;
          await this.jobService.createJob(job, true);
          this.resetFormStateAfterSave();
          this.historyBack();
        }
      } else {
        this.toastService.add({ type: 'ERROR', message: 'noCustomerAssigned' });
      }
    }
  }

  toggleUndecided(event: MatSlideToggleChange) {
    this.undecided = event.checked;
    this.hasToggledUndecided = true;
    this.filterUsers().then();
  }

  toggleIgnoreUserPreferenceDepartmentBlacklist(event: MatSlideToggleChange) {
    this.ignoreUserPreferenceDepartmentBlacklist = event.checked;
    this.filterUsers().then();
  }

  toggleExternalInvitees() {
    this.hasToggledExternalInvitees = true;
    this.filterUsers().then();
  }

  toggleHasMultipleShifts(checked: boolean, manuelTriggeredEvent?: boolean) {
    this.job.shifts = [];
    this.hasMultipleShifts =
      manuelTriggeredEvent !== undefined ? manuelTriggeredEvent : checked;
    this.filterUsers().then();
  }

  manualTransfer(targetCustomer: any) {
    if (targetCustomer) {
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        data: {
          id: this.job._id,
          text:
            (this.i18n?.transferNowPre ||
              'Möchten Sie diesen Dienst sofort an') +
            ' ' +
            targetCustomer.displayName +
            ' ' +
            (this.i18n?.transferNowPost || 'transferieren?'),
        },
      });
      dialogRef.afterClosed().subscribe((result: string) => {
        if (result) {
          let transfer = this.job?.transfers?.find(
            (transfer) => transfer.targetCustomer.id === targetCustomer.id,
          );
          if (!transfer) return;
          this.hasTransferred = true;
          Meteor.call(
            'transferJob',
            {
              jobId: this.job._id,
              transfer,
              confirmation: this.job.transferConfirmation,
              isReopen: false,
            },
            (error: Meteor.Error) => {
              if (error) {
                this.toastService.add({
                  type: 'ERROR',
                  message: 'serverError',
                });
                return;
              }
              this.toastService.add({
                type: 'SUCCESS',
                message: 'jobTransferred',
              });
            },
          );
        }
      });
    }
  }

  manualTransferAll() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: this.job._id,
        text:
          (this.i18n?.transferNowPre || 'Möchten Sie diesen Dienst sofort an') +
          ' ' +
          (this.i18n?.transferNowAll || 'alle Vermittler') +
          ' ' +
          (this.i18n?.transferNowPost || 'transferieren?'),
      },
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        this.hasTransferred = true;
        Meteor.call(
          'transferJobToAll',
          this.job._id,
          this.job.transfers,
          this.job.transferConfirmation,
          (error: Meteor.Error) => {
            if (error) {
              this.toastService.add({
                type: 'ERROR',
                message: 'serverError',
              });
              return;
            }
            this.toastService.add({
              type: 'SUCCESS',
              message: 'jobTransferred',
            });
          },
        );
      }
    });
  }

  multiDayInviteeToggled(event: InviteeToggledEvent, multiDay: MultiDay) {
    if (event.type === 'INVITE') {
      if (event.inviteeSelected) {
        const isAlreadySelected = multiDay.inviteUsers?.some(
          (user) => user._id == event.user._id,
        );
        if (!isAlreadySelected) {
          multiDay.inviteUsers.push(event.user);
          // remove from directAccept list
          multiDay.directAcceptUsers = multiDay.directAcceptUsers.filter(
            (user) => user._id !== event.user._id,
          );
        }
      } else {
        multiDay.inviteUsers = multiDay.inviteUsers.filter(
          (user) => user._id !== event.user._id,
        );
      }
    } else if (event.type === 'DIRECT_ACCEPT') {
      if (event.inviteeSelected) {
        const isAlreadySelected = multiDay.directAcceptUsers?.some(
          (user) => user._id == event.user._id,
        );
        if (!isAlreadySelected) {
          multiDay.directAcceptUsers.push(event.user);
          // remove from invite list
          multiDay.inviteUsers = multiDay.inviteUsers.filter(
            (user) => user._id !== event.user._id,
          );
        }
      } else {
        multiDay.directAcceptUsers = multiDay.directAcceptUsers.filter(
          (user) => user._id !== event.user._id,
        );
      }
      const desiredRestHours = 11;
      const violationsPerDayUuid = checkMultiDayRest(
        this.multiDays,
        desiredRestHours,
      );
      this.violationMessagesPerDayUuid =
        this.getViolationMessagesPerDayUuid(violationsPerDayUuid);
    }

    this.multiDayInviteUsers = 0;
    this.multiDayDirectAcceptUsers = 0;
    this.multiDays.forEach((multiDay) => {
      this.multiDayInviteUsers += multiDay.inviteUsers.length;
      this.multiDayDirectAcceptUsers += multiDay.directAcceptUsers.length;
    });

    this.updateCachedSelection();
  }

  getViolationMessagesPerDayUuid(
    violationsPerDayUuid: ViolationsPerDayUuid,
  ): Map<
    string,
    {
      overlapsMessage: string;
      restViolationsMessage: string;
      overlaps: RestViolationResult[];
      restViolations: RestViolationResult[];
    }
  > {
    return new Map(
      Array.from(violationsPerDayUuid.entries()).map(([uuid, violations]) => [
        uuid,
        {
          overlapsMessage: violations.overlaps
            .map(this.getMessageFromRestViolation.bind(this))
            .join('\n\n'),
          restViolationsMessage: violations.restViolations
            .map(this.getMessageFromRestViolation.bind(this))
            .join('\n\n'),
          overlaps: violations.overlaps,
          restViolations: violations.restViolations,
        },
      ]),
    );
  }
  getMessageFromRestViolation(violation: RestViolationResult): string {
    const locale = this.translationService.getDateFnsLocale();
    const firstShift =
      format(violation.firstShift.start, 'EEE dd.MM.yyyy HH:mm', { locale }) +
      ' - ' +
      format(violation.firstShift.end, 'HH:mm', { locale });
    const secondShift =
      format(violation.secondShift.start, 'EEE dd.MM.yyyy HH:mm', { locale }) +
      ' - ' +
      format(violation.secondShift.end, 'HH:mm', { locale });
    return `${violation.user.firstName} ${violation.user.lastName}\n  ${firstShift}\n  ${secondShift}`;
  }

  dayInviteeToggled(event: InviteeToggledEvent) {
    if (event.type === 'INVITE') {
      if (
        event.inviteeSelected &&
        this.selectedUsers.findIndex((user1) => user1._id == event.user._id) ==
          -1
      ) {
        this.ngZone.run(() => {
          let directAcceptUserIndex = this.directAcceptUsers.findIndex(
            (user) => user._id == event.user._id,
          );
          if (directAcceptUserIndex !== -1) {
            this.directAcceptUsers.splice(directAcceptUserIndex, 1);
          }
          this.selectedUsers.push(event.user);
        });
      } else if (!event.inviteeSelected) {
        let userIndex = this.selectedUsers.findIndex(
          (user) => user._id == event.user._id,
        );
        this.ngZone.run(() => {
          setTimeout(() => {
            userIndex > -1 ? this.selectedUsers.splice(userIndex, 1) : false;
          }, 0);
        });
      }
    }
    if (event.type === 'DIRECT_ACCEPT') {
      if (
        event.inviteeSelected &&
        this.directAcceptUsers.findIndex(
          (user1) => user1._id == event.user._id,
        ) == -1
      ) {
        this.ngZone.run(() => {
          let selectedUserIndex = this.selectedUsers.findIndex(
            (user) => user._id == event.user._id,
          );
          if (selectedUserIndex !== -1) {
            this.selectedUsers.splice(selectedUserIndex, 1);
          }
          this.directAcceptUsers.push(event.user);
        });
      } else if (!event.inviteeSelected) {
        let userIndex = this.directAcceptUsers.findIndex(
          (user) => user._id == event.user._id,
        );
        this.ngZone.run(() => {
          setTimeout(() => {
            userIndex > -1
              ? this.directAcceptUsers.splice(userIndex, 1)
              : false;
          }, 0);
        });
      }
    }
    this.updateCachedSelection();
  }

  declineTransfer() {
    // @ts-ignore
    Meteor.apply('declineTransfer', [this.job._id], {
      wait: false,
      onResultReceived: (error) => {
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.toastService.add({ type: 'SUCCESS', message: 'transferDeclined' });
        this.historyBack();
      },
    });
  }

  close() {
    Meteor.call('declineAndClose', this.job._id, (error: Meteor.Error) => {
      if (error) {
        this.toastService.add({ type: 'ERROR', message: 'serverError' });
        return;
      }
      this.toastService.add({ type: 'SUCCESS', message: 'jobClosed' });
      this.location.back();
    });
  }

  async filterMultiDayUsers() {
    const poolerMap = await firstValueFrom(
      this.poolerService.readyPoolerCustomerMap$,
    );
    this.syncCustomerInternalInformationToJob();
    this.loadingUsers = true;
    Meteor.setTimeout(() => {
      this.multiDays
        .filter((day) => day.enabled)
        .forEach((day) => {
          day.possibleUsers = getPossibleUsersForJob({
            date: day.date,
            job: this.job,
            shifts: day.shifts,
            department: this.selectedDepartment!,
            loggedInCustomer: this.loggedInCustomer!,
            sharedPoolCustomers: this.sharedPoolCustomers,
            poolerMap: poolerMap,
            user: this.user!,
            i18n: this.i18n!,
            isOwnJob: this._isOwnJob(),
            undecided: this.undecided,
            ignoreUserPreferenceDepartmentBlacklist: false,
            jobInPast: this.jobInPast,
            search: day.userSearch,
            transferToMe: this.transferToMe,
          });
          if (this.paramUserId) {
            if (this.paramBookDirectly) {
              day.directAcceptUsers = day.possibleUsers.filter(
                (user) => user._id == this.paramUserId,
              );
            } else {
              day.inviteUsers = day.possibleUsers.filter(
                (user) => user._id == this.paramUserId,
              );
            }
          }
        });
      this.applyCachedSelectionForMultiDays();
      this.updateUserCount();
      this.checkRestViolations();
      this.loadingUsers = false;
    }, 100);
  }

  private updateUserCount() {
    this.userCount = new Map<string, number>();
    this.multiDays
      .filter((day) => day.enabled)
      .forEach((day) => {
        day.possibleUsers
          .filter((u) => !u.isUndecided)
          .forEach((u) => {
            if (this.userCount.get(u._id)) {
              this.userCount.set(u._id, this.userCount.get(u._id)! + 1);
            } else {
              this.userCount.set(u._id, 1);
            }
          });
      });
    this.multiDays.forEach((day) => this.sortPossibleUsers(day));
  }

  getNewJob() {
    this.randomId = randomId();
    Session.set(this.randomId, {});
    let newJob: Job;
    this.editor = this.user?._id;
    if (this.route.snapshot.queryParamMap.get('clone')) {
      let jobId = this.route.snapshot.queryParamMap.get('clone');
      this.jobSubscription = MeteorObservable.subscribe('job', jobId).subscribe(
        async () => {
          if (!jobId || !this.loggedInCustomer) {
            return;
          }
          newJob = Jobs.findOne(jobId);
          newJob.invitees = [];
          newJob.status = JobStatusEnum.OPEN;
          newJob.customer = {
            id: this.loggedInCustomer._id,
            displayName: this.loggedInCustomer.name,
          };
          if (this.loggedInCustomer.enableCopyDateOnDuplicate) {
            newJob.date = newJob.date;
          }
          // @ts-ignore
          delete newJob._id;
          // @ts-ignore
          delete newJob.orderId;
          // @ts-ignore
          delete newJob.createdAt;
          // @ts-ignore
          delete newJob.updatedAt;
          // @ts-ignore
          delete newJob.updatedBy;
          // @ts-ignore
          delete newJob.archiveAt;
          if (this.loggedInCustomer.enableCopyDateOnDuplicate) {
            this.dateRanges = [{ from: new Date(newJob.date) }];
          } else {
            // @ts-ignore
            delete newJob.date;
            this.dateRanges = [{ from: undefined }];
          }
          delete newJob.requestedAt;
          delete newJob.requestRetractedAt;
          delete newJob.cancelReason;
          delete newJob.canceledAt;
          delete newJob.canceledBy;
          delete newJob.transfers;
          delete newJob.multiDayId;
          delete newJob.transferConfirmation;
          delete newJob.branch;
          this.job = newJob;
          const clonedShifts = newJob.shifts;
          const departmentWithOrganisation =
            await this.organisationService.getOrganisationAndDepartmentById(
              this.job.department.id,
            );
          if (!departmentWithOrganisation) {
            return;
          }
          const { department, organisation } = departmentWithOrganisation;
          this.selectedDepartment = {
            ...department,
            shifts: department.shifts.map((shift) => ({ ...shift })),
          };
          this.job.department = {
            id: department.id,
            name: department.name,
            phone: department.phone,
            costCenter: department.costCenter,
            clinic: department.clinic,
            section: department.section,
            organisationalUnit: department.organisationalUnit,
          };
          this.job.organisation = {
            id: organisation._id,
            name: organisation.name,
            address: organisation.address,
          };
          if (organisation.ownerCustomerIds?.length) {
            this.job.ownerCustomerIds = organisation.ownerCustomerIds;
          }
          this.selectedDepartmentId = department.id;
          this.job.shifts = clonedShifts;
          const clonedShiftsId = clonedShifts.map((shift) => shift.id);
          this.ngZone.run(() => {
            clonedShifts.forEach((shift) => {
              const departmentShift = this.selectedDepartment?.shifts.find(
                (departmentShift) => departmentShift.shift === shift.shift,
              );
              if (!departmentShift) {
                return;
              }
              departmentShift.from = shift.from;
              departmentShift.to = shift.to;
            });
            if (this.job.shifts.length > 1) {
              this.toggleHasMultipleShifts(true, true);
            }
            this.job.shifts =
              this.selectedDepartment?.shifts.filter((shift) =>
                clonedShiftsId.includes(shift.id),
              ) || [];
            this.selectShift();
          });
          this.ngZone.run(() => {
            this.form?.form.markAsDirty();
          });
          this.jobSource$.next(newJob);
        },
      );
    } else {
      this.branch = this.user?.branch;
      this.dateRanges = [{ from: undefined }];
      if (!this.loggedInCustomer) {
        return;
      }
      // @ts-ignore
      newJob = {
        date: undefined,
        shifts: [],
        certificates: [],
        specialities: [],
        status: JobStatusEnum.OPEN,
        invitees: [],
        requiredUsers: 1,
        customer: {
          id: this.loggedInCustomer._id,
          displayName: this.loggedInCustomer.name,
        },
        jobType: JobTypeEnum.NORMAL,
        allowExternalInvitees:
          !this.loggedInCustomer.externalPoolerOffByDefault,
      } as Job;
      this.job = newJob;
      this.jobSource$.next(newJob);

      this.subscriptionService.jobUsersReady$.subscribe(() => {
        this.loadingUsers = false;
        if (!this.loadingUsers) {
          this.applyQueryParamsToJob().then();
        }
      });
    }
  }

  private getInvitees() {
    if (
      this.job &&
      this.job.invitees &&
      this.job.invitees.length > 0 &&
      this.user?.customerId
    ) {
      const invitees = getInviteesOfJob(
        this.job,
        this.job.invitees,
        this.user.customerId,
        this.sharedPoolCustomers,
      );
      let acceptedInvitees = invitees
        .filter((invitee) => invitee.status == InviteeStatusEnum.BOOKED)
        .map((invitee) => invitee.userId);
      let availableInvitees = invitees
        .filter((invitee) =>
          [
            InviteeStatusEnum.AVAILABLE,
            InviteeStatusEnum.POSSIBLE_CANDIDATE,
          ].includes(invitee.status),
        )
        .map((invitee) => invitee.userId);
      this.ngZone.run(() => {
        this.acceptedUsers = (
          Users.find({
            _id: { $in: acceptedInvitees },
          }) as Observable<PoolerUser[]>
        ).pipe(rxNgZoneRun(this.ngZone));
        this.availableUsers = (
          Users.find({
            _id: { $in: availableInvitees },
          }) as Observable<PoolerUser[]>
        ).pipe(rxNgZoneRun(this.ngZone));
        this.hasAvailableOrAcceptedInvitees = invitees.some((invitee) =>
          [
            InviteeStatusEnum.BOOKED,
            InviteeStatusEnum.POSSIBLE_CANDIDATE,
            InviteeStatusEnum.AVAILABLE,
            InviteeStatusEnum.PARTIAL_AVAILABLE,
          ].includes(invitee.status),
        );

        this.hasAcceptedInvitee = invitees.some(
          (invitee) => invitee.status == InviteeStatusEnum.BOOKED,
        );
      });
    }
  }

  private _resetDepartment() {
    this.selectedDepartment = undefined;
    // @ts-ignore
    delete this.job.shifts;
    // @ts-ignore
    delete this.job.department;
    this.selectedDepartmentId = undefined;
    this.hasMultipleShifts = false;
    // @ts-ignore
    delete this.possibleUsers;
    this.multiDays = [];
    this.selectedUsers = [];
    this.directAcceptUsers = [];
    this.clearInviteeSelection();
  }

  private async modifyMultiDaysJobType(jobType: JobTypeEnum) {
    this.multiDays.forEach((day) => {
      day.jobType = jobType;
    });
    // reset shift for departments where the multiDay shift does not match the jobType
    this.multiDays
      .filter((d) =>
        d.shifts.every(
          (s) => s.specificJobTypes && !s.specificJobTypes?.includes(jobType),
        ),
      )
      .forEach((d) => {
        const availableShift = d.availableDepartmentShifts.find(
          (s) => !s.specificJobTypes || s.specificJobTypes?.includes(jobType),
        );
        if (availableShift) {
          d.shifts = [availableShift];
        }
      });
    this.job.jobType = jobType;
    this.jobSource$.next(this.job);
    this.filterMultiDayUsers();
  }

  private async _updateMultiDays() {
    const department = this.selectedDepartment;
    if (!department) {
      console.error('missing department in _updateMultiDays');
      return;
    }
    if (this.dateRanges.length) {
      const dates = getDatesFromRanges(this.dateRanges);
      dates.sort((a, b) => a.getTime() - b.getTime());
      this.multiDays = dates.map((date) => ({
        date: format(date, dbDayDateToken),
        possibleUsers: [],
        enabled: true,
        inviteUsers: [],
        directAcceptUsers: [],
        requiredUsers: this.job.requiredUsers,
        jobType: this.job.jobType,
        collapsed: true,
        userSearch: '',
        shifts: this.job.shifts,
        availableDepartmentShifts: this.getAvailableShifts(date, department),
        uuid: randomId(),
        jobInPast: addDays(date, 1) < new Date(),
      }));
      this.applyCachedSelectionForMultiDays();
      await this.filterUsers();
    }
  }

  private async _updateJob(job: Job): Promise<Job> {
    let now = getNewUnixTimestamp();
    if (!this.loggedInCustomer) {
      throw new Meteor.Error('not-authorized');
    }
    job.customer = {
      id: this.loggedInCustomer._id,
      displayName: this.loggedInCustomer.name,
    };
    job.createdAt = now;
    job.updatedAt = now;
    job.createdBy = await this.getJobCreatedBy(this.editor);
    job.branch = this.branch;
    job.transfers?.forEach((transfer, i) => {
      if (job.transfers && transfer.directTransfer) {
        job.transfers[i].transferAt = now;
      }
    });
    job.customerInternalInformation = {
      [this.loggedInCustomer._id]: this.customerInternalInformation,
    };
    const organisation = Organisations.findOne({
      'departments.id': job.department.id,
    });
    if (organisation?.ownerCustomerIds?.length) {
      job.ownerCustomerIds = organisation.ownerCustomerIds;
    }
    this.job.jobStart = getJobStart(this.job.shifts, this.job.date);
    return job;
  }

  async getJobCreatedBy(userId?: string): Promise<Job['createdBy']> {
    if (!userId) {
      throw new Error("missing Editor user to save 'job.createdBy'");
    }
    const editorUser = await firstValueFrom(
      this.userService.getDispoById(userId),
    );
    return {
      userId,
      firstName: editorUser.firstName,
      lastName: editorUser.lastName,
    };
  }

  private _setTransfers(job: Job) {
    setTransfersForJob({
      job,
      customer: this.loggedInCustomer,
      startDate: this.dateRanges[0].from,
    });
  }

  private _setMultiDayTransfers(job: Job) {
    if (job.date && job.shifts?.length && job.transfers) {
      let jobStartTimeUtc = getUnixTime(
        addTimeZoneOffset(
          addMinutes(
            addHours(new Date(job.date), Math.floor(job.shifts[0].from / 100)),
            job.shifts[0].from % 100,
          ),
        ),
      );
      job.transfers.forEach((transfer) => {
        const offset =
          this.loggedInCustomer?.customerTransfers?.transfers?.find(
            (customerTransfer) =>
              customerTransfer.targetCustomer === transfer.targetCustomer,
          )?.redirectOffset || 0;
        if (transfer.directTransfer) {
          transfer.transferAt = getNewUnixTimestamp();
        } else {
          transfer.transferAt = jobStartTimeUtc - offset;
        }
      });
      return job.transfers;
    }
  }

  openAgentDialog(availabilityInfosForAgents: string, customerName: string) {
    this.dialog.open(AgentsInfoDialog, {
      data: {
        title: customerName,
        html: availabilityInfosForAgents,
      },
    });
  }

  private _setArchiveAt(job: Job) {
    if (!job.shifts?.length || !this.loggedInCustomer || !job.date) {
      return;
    }
    job.archiveAt = getArchiveAt(this.loggedInCustomer, job);
  }

  toggleDayEnabled(day: MultiDay) {
    day.enabled = !day.enabled;
    this.filterUsers().then();
  }

  transfersConfirmedChange() {
    if (!this.user || !this.loggedInCustomer || this.hasTransferred) return;

    if (this.transferConfirmed && this.selectedTransferConfirmationPerson) {
      this.job.transferConfirmation = {
        confirmedAt: getNewUnixTimestamp(),
        confirmedPerson: this.selectedTransferConfirmationPerson,
        confirmedBy: {
          userId: this.user!._id,
          firstName: this.user!.firstName,
          lastName: this.user!.lastName,
        },
      };
    } else {
      this.job.transfers?.forEach((transfer) => {
        transfer.auto = false;
      });
      delete this.job.transferConfirmation;
    }
  }

  getAvailableShifts(date: Date, department: Department): Shift[] {
    const weekDay = getWeekDayEnumByIndex(getDay(date));
    return this.getShiftsForWeekday(weekDay);
  }

  editMultiDayShifts(
    day: MultiDay,
    { shifts, jobType }: { shifts: Shift[]; jobType?: JobTypeEnum },
  ) {
    day.jobType = jobType || JobTypeEnum.NORMAL;
    const stillHasSameShifts =
      JSON.stringify(day.shifts.map((shift) => shift.id)) ===
      JSON.stringify(shifts.map((shift) => shift.id));
    if (stillHasSameShifts) {
      day.shifts = shifts;
      this.checkRestViolations();
    } else {
      day.shifts = shifts;
      this.updateUsersForMultiDay(day).then();
    }
  }

  async updateUsersForMultiDay(day: MultiDay) {
    const poolerMap = await firstValueFrom(
      this.poolerService.readyPoolerCustomerMap$,
    );
    this.syncCustomerInternalInformationToJob();

    // Reset inviteUsers and directAcceptUsers
    day.inviteUsers = [];
    day.directAcceptUsers = [];

    // Fetch new possibleUsers
    day.possibleUsers = getPossibleUsersForJob({
      date: day.date,
      job: this.job,
      shifts: day.shifts,
      department: this.selectedDepartment!,
      loggedInCustomer: this.loggedInCustomer!,
      sharedPoolCustomers: this.sharedPoolCustomers,
      poolerMap: poolerMap,
      user: this.user!,
      i18n: this.i18n!,
      isOwnJob: this._isOwnJob(),
      undecided: this.undecided || false,
      ignoreUserPreferenceDepartmentBlacklist: false,
      jobInPast: this.jobInPast,
      search: day.userSearch,
      transferToMe: this.transferToMe,
    });

    // Update user count
    this.updateUserCount();

    // Check for rest violations
    this.checkRestViolations();
    this.applyCachedSelectionForMultiDay(day);
  }

  private sortPossibleUsers(day: MultiDay) {
    const lastNamePriority = this.loggedInCustomer?.prioritizeEmployeeLastName;

    day.possibleUsers = day.possibleUsers
      .sort((a, b) =>
        lastNamePriority
          ? a.lastName.localeCompare(b.lastName)
          : a.firstName.localeCompare(b.firstName),
      )
      .sort(
        (a, b) =>
          (this.userCount.get(b._id) || 0) - (this.userCount.get(a._id) || 0),
      );
  }

  private checkRestViolations() {
    const desiredRestHours = 11;
    const violationsPerDayUuid = checkMultiDayRest(
      this.multiDays,
      desiredRestHours,
    );
    this.violationMessagesPerDayUuid =
      this.getViolationMessagesPerDayUuid(violationsPerDayUuid);
  }

  selectDemandReason(reasonId: string) {
    let demandReason = this.loggedInCustomer?.demandReasons?.find(
      (reason) => reason.id === reasonId,
    );
    if (demandReason) {
      this.customerInternalInformation.demandReason = demandReason;
    }
  }

  // Helper methods for pooler types
  arraysEqual(arr1: string[] | undefined, arr2: string[] | undefined): boolean {
    if (!arr1 && !arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    return arr1.sort().join(',') === arr2.sort().join(',');
  }

  getPoolerTypeDisplayName(poolerTypeId: string): string {
    const poolerType = this.loggedInCustomer?.poolerTypes?.find(
      (pt) => pt.id === poolerTypeId,
    );
    return poolerType?.displayName || poolerTypeId;
  }

  readonly DispoJobStatus = DispoJobStatus;

  navigateToMultiDayEdit() {
    if (this.job?.multiDayId) {
      this.router.navigate(['/multiday-job', this.job.multiDayId]).then();
    }
  }

  clearRequestRetraction(event: Event, approve: Boolean) {
    event.stopPropagation();
    if (approve) {
      this.jobRequestService.approveRetractionOnRequest(this.job._id).then();
    } else {
      this.jobRequestService.rejectRetractionOnRequest(this.job._id).then();
    }
    delete this.job.requestRetractionRequestedAt;
    delete this.job.requestRetractionRequestedBy;
    this.jobSource$.next(this.job);
  }

  hasUnsavedChanges(): boolean {
    if (this.form?.dirty) {
      return true;
    }
    if (this.selectedUsers.length > 0) {
      return true;
    }
    if (this.directAcceptUsers.length > 0) {
      return true;
    }
    if (
      this.multiDays.some(
        (day) => day.inviteUsers.length > 0 || day.directAcceptUsers.length > 0,
      )
    ) {
      return true;
    }

    return false;
  }
}

export class TimeErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      formatTimeNumber(control.value) == 'Invalid' &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
